/**
 * АЖЛЫН ЦАГИЙН ХУВААРЬ — ДАВУУ ЭРЭМБИЙН РЕЗОЛВЕР + API ↔ АДМИНЫ ГЭРЭЭ (docs/12 §Ажлын цагийн хуваарь, docs/48 §8.9).
 *
 * ! Шинэ зэрэгцээ ойлголт БИШ: «хуваарь» = одоо байгаа `timesheet_calendars` (+ `timesheet_calendar_schedules` = гарагийн хэв маяг,
 * ! ГАНЦ ЖИНХЭНЭ ЭХ). `weekly_schedule` JSON нь зөвхөн уншигддаг КЭШ — резолвер түүнийг УНШИХГҮЙ.
 *
 * ! ДАВУУ ЭРЭМБЭ (эзний шийдвэр, 2026-09-29, эцсийн):
 * !   1. employee      — хувь хүний онцгой тохиргоо;
 * !   2. job_position  — ТУХАЙН ӨДРИЙН албан тушаал;
 * !   3. department    — ТУХАЙН ӨДРИЙН алба, хуваарилалтгүй бол `parentId`-аар өгсөж ХАМГИЙН ОЙРЫН өвгийнх (гүн ≤ 20, мөчлөгөөс хамгаална);
 * !   4. organization  — ажилтны `organizationId`, байхгүй бол албаны ҮЙЛЧЛЭХ байгууллага (`effectiveDepartmentOrganization`);
 * !   5. global        — миграц 5-р хуанли, `valid_from = 1900-01-01`-ээр үүсгэнэ;
 * !   6. юу ч үгүй     — `source = 'none'` (өнөөдрийн «хуваарьгүй өдөр» зан төлөв).
 * ! Албан тушаал нь ажилтны албаны ӨВӨГ албанд харьяалагдаж болно (`hr-employees.service` — удмын гинжээр шалгадаг); тэр үед ч
 * ! АЛБАН ТУШААЛЫН дүрэм давамгайлна (2 < 3).
 *
 * ! `hr_employees.calendar_id` = 5 нь ХУВИЙН ТОХИРГОО БИШ (эзний шийдвэр) — резолвер тэр баганыг огт уншихгүй. 5-аас ялгаатай
 * ! утгуудыг миграц `scope = 'employee'` хуваарилалт болгон хөрвүүлсэн.
 *
 * ! ОГНООТОЙ: хуваарилалт (`valid_from ≤ D ≤ valid_to`) ба хэв маяг (`valid_from` хувилбар) хоёулаа огноотой — өнгөрсөн өдрийг
 * ! дахин бодоход ТЭР ӨДРИЙН хүчинтэй хуваарь авагдана, түүх чимээгүй өөрчлөгдөхгүй.
 *
 * ! ЦЭВЭР — `Date` БАЙХГҮЙ. Огноо нь `YYYY-MM-DD` мөр (Улаанбаатарын хуанлийн өдөр), цаг нь `HH:MM` ханын цаг. Гарагийг
 * ! иргэний хуанлийн арифметикаар бодно — процессын цагийн бүс (машин UTC+8, прод UTC) нөлөөлөх ЗАМ Ч үгүй.
 * ! Id-г МӨР болгож өгнө (API нь `BigInt`-ийг `String()`-ээр хөрвүүлнэ).
 *
 * ! ХАМРАХ ХҮРЭЭНЭЭС ГАДУУР (эзний шийдвэр): шөнийн ээлж / өдрийн огтлол (`day_boundary`) — API `end ≤ start`-ийг татгалзана;
 * ! аппын (GPS) ирц.
 */

import { effectiveDepartmentOrganization } from './hr-organization.js'

/* ─────────────────────────── Толь ─────────────────────────── */

/** Хуваарилалтын түвшин — давуу эрэмбээр (эхнийх нь хамгийн хүчтэй). */
export const WORK_SCHEDULE_SCOPES = ['employee', 'job_position', 'department', 'organization', 'global'] as const

export type WorkScheduleScope = (typeof WORK_SCHEDULE_SCOPES)[number]

/** Резолверийн эх сурвалж — `timesheet_daily_reports.schedule_source`-той ИЖИЛ толь. */
export const WORK_SCHEDULE_SOURCES = [...WORK_SCHEDULE_SCOPES, 'none'] as const

export type WorkScheduleSource = (typeof WORK_SCHEDULE_SOURCES)[number]

/**
 * Гарагийн нэр — `timesheet_calendar_schedules.day_of_week`, `weekly_schedule` JSON-ы түлхүүртэй ИЖИЛ (эх системийн).
 * ! ДАРААЛАЛ нь Даваагаас эхэлнэ (засварлагчийн 7 мөр) — `getUTCDay()`-ийн индекс БИШ.
 */
export const WORK_SCHEDULE_WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const

export type WorkScheduleWeekday = (typeof WORK_SCHEDULE_WEEKDAYS)[number]

/** Глобал өгөгдмөл хуанли — эх системийн `$defaultCalendarId` («40 цаг/ажлын 5 өдөрт», Да–Ба 07:30–17:00, завсарлагагүй). */
export const WORK_SCHEDULE_GLOBAL_CALENDAR_ID = '5'

/** Хуучин хэв маягийн мөрүүдийн хувилбар — миграцийн `valid_from` өгөгдмөл. */
export const WORK_SCHEDULE_LEGACY_VALID_FROM = '1900-01-01'

/** Хоцролтын хөнгөлөлтийн өгөгдмөл — 0 минут (эзний шийдвэр; одоогийн төхөөрөмжийн дүрэм). */
export const WORK_SCHEDULE_DEFAULT_GRACE_MINUTES = 0

/** Хөнгөлөлтийн дээд хязгаар — API баталгаажуулалт. */
export const WORK_SCHEDULE_MAX_GRACE_MINUTES = 240

/**
 * Бэкдейтийн баталгаа шаардсан 400-ийн `code` — API (`HrWorkSchedulesService.backdateRequired`) ба админ (баталгаажуулах цонх)
 * НЭГ мөр. Хариуны бие: `{ code, message, affectedReports }`; админ баталгаа авсны дараа `confirmBackdate: true`-тэй давтана.
 */
export const WORK_SCHEDULE_BACKDATE_CONFIRM_CODE = 'WORK_SCHEDULE_BACKDATE_CONFIRM_REQUIRED'

/** Албаны эцэг рүү өгсөх ДЭЭД гүн — мөчлөг/гажиг өгөгдлөөс хамгаална. */
export const WORK_SCHEDULE_DEPARTMENT_MAX_DEPTH = 20

/* ─────────────────────────── Огноо, цаг (цэвэр) ─────────────────────────── */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

/** Иргэний хуанлийн өдрийн дугаар (1970-01-01 = 0) — Howard Hinnant-ийн `days_from_civil`. */
const daysFromCivil = (year: number, month: number, day: number): number => {
  const y = month <= 2 ? year - 1 : year
  const era = Math.floor(y / 400)
  const yoe = y - era * 400
  const mp = (month + 9) % 12
  const doy = Math.floor((153 * mp + 2) / 5) + day - 1
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy

  return era * 146097 + doe - 719468
}

const civilFromDays = (days: number): [number, number, number] => {
  const z = days + 719468
  const era = Math.floor(z / 146097)
  const doe = z - era * 146097
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365)
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100))
  const mp = Math.floor((5 * doy + 2) / 153)
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1
  const month = mp < 10 ? mp + 3 : mp - 9

  return [yoe + era * 400 + (month <= 2 ? 1 : 0), month, day]
}

/** `YYYY-MM-DD` мөн эсэх — хуанлид БАЙГАА өдөр (2026-02-30 → false). */
export const isWorkScheduleDate = (value: unknown): value is string => {
  if (typeof value !== 'string') return false

  const match = DATE_RE.exec(value)

  if (!match) return false

  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]

  if (month < 1 || month > 12 || day < 1) return false

  const [y, m, d] = civilFromDays(daysFromCivil(year, month, day))

  return y === year && m === month && d === day
}

const dayNumber = (date: string): number => {
  const match = DATE_RE.exec(date)

  if (!match) throw new Error(`Огноо YYYY-MM-DD биш: ${date}`)

  return daysFromCivil(Number(match[1]), Number(match[2]), Number(match[3]))
}

/** `D + n` өдөр (`n` сөрөг байж болно) — хуваарилалт хаахад `valid_to = valid_from − 1`. */
export const addWorkScheduleDays = (date: string, days: number): string => {
  const [y, m, d] = civilFromDays(dayNumber(date) + days)

  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** Өдрийн гараг — `new Date(\`${D}T00:00:00Z\`).getUTCDay()`-тэй ижил, гэхдээ `Date`-гүй. */
export const workScheduleWeekday = (date: string): WorkScheduleWeekday => {
  // 1970-01-01 = Пүрэв (индекс 3 нь Даваагаас эхэлсэн жагсаалтад).
  const index = (((dayNumber(date) + 3) % 7) + 7) % 7

  return WORK_SCHEDULE_WEEKDAYS[index]!
}

/** `HH:MM` мөн эсэх (00:00–23:59). */
export const isWorkScheduleTime = (value: unknown): value is string => typeof value === 'string' && HHMM_RE.test(value)

/**
 * Эх системийн чөлөөт цагийг `HH:MM` болгоно: `"07:30:00"`, `"9"`, `"17:00"` → `07:30`, `09:00`, `17:00`
 * (`attendance-calculator.service.ts` `normaliseHour`-тэй ИЖИЛ дүрэм). Таньж чадахгүй бол `null`.
 */
export const normalizeWorkScheduleTime = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) return null

  const trimmed = value.trim()

  if (/^\d{1,2}$/.test(trimmed)) {
    const hours = Number(trimmed)

    return hours <= 23 ? `${String(hours).padStart(2, '0')}:00` : null
  }

  const match = /^(\d{1,2}):(\d{2})/.exec(trimmed)

  if (!match) return null

  const normalized = `${match[1]!.padStart(2, '0')}:${match[2]}`

  return HHMM_RE.test(normalized) ? normalized : null
}

/** `HH:MM` → өдрийн эхнээс хэдэн минут. */
export const workScheduleMinutes = (hhmm: string): number => {
  const match = HHMM_RE.exec(hhmm)

  if (!match) throw new Error(`Цаг HH:MM биш: ${hhmm}`)

  return Number(match[1]) * 60 + Number(match[2])
}

/* ─────────────────────────── Резолверийн оролт ─────────────────────────── */

/** Хэв маягийн нэг мөр (`timesheet_calendar_schedules`, `deleted_at IS NULL`). */
export type WorkSchedulePatternRow = {
  weekday: string | null
  validFrom: string
  isWorkDay: boolean
  hourFrom: string | null
  hourTo: string | null
  breakFrom?: string | null
  breakTo?: string | null
  /**
   * Хоцролтын хөнгөлөлт (минут) — ХУВИЛБАРЫН хэсэг (`timesheet_calendar_schedules.grace_minutes`, 7 мөрд ижил).
   * ! Хувилбарынх нь ЯЛНА: өнгөрсөн өдрийг дахин бодоход тэр үеийн хөнгөлөлт авагдана. Байхгүй (`undefined`/`null`) бол
   * ! `WorkScheduleCalendarInput.graceMinutes` (хуанлийн кэш).
   */
  graceMinutes?: number | null
}

export type WorkScheduleCalendarInput = {
  id: string
  name: string
  /** Өнөөдрийн хувилбарын хөнгөлөлтийн КЭШ — хувилбарын мөрөнд `graceMinutes` байхгүй үед л ашиглана. */
  graceMinutes: number
  rows: readonly WorkSchedulePatternRow[]
}

/** Хуваарилалт (`timesheet_schedule_assignments`, `deleted_at IS NULL`). `targetId` — `global` бол `null`. */
export type WorkScheduleAssignmentInput = {
  id: string
  calendarId: string
  scope: WorkScheduleScope
  targetId: string | null
  validFrom: string
  validTo: string | null
}

export type WorkScheduleDepartmentNode = {
  id: string
  parentId: string | null
  organizationId: string | null
}

/** Албан тушаалын түүхийн мөр (`hr_employee_resumes`). */
export type WorkScheduleResumeRow = {
  id: string
  departmentId: string | null
  jobPositionId: string | null
  startDate: string | null
  endDate: string | null
}

export type WorkSchedulePlacement = {
  departmentId: string | null
  jobPositionId: string | null
  /** `resume` — D өдрийг хамарсан түүхийн мөр; `current` — `hr_employees.current_*`. */
  from: 'resume' | 'current'
  resumeId: string | null
}

/**
 * D ӨДРИЙН алба, албан тушаал.
 *
 * ! `startDate ≤ D ≤ endDate` (`endDate` хоосон = нээлттэй) мөрүүдээс ХАМГИЙН ТОМ id-тай нь — систем «сүүлд БИЧСЭН мөр ялна»
 * ! дүрмээр одоогийн байрлалыг тооцдог (`hr-employees.service` `syncCurrentPosition`; эх баазад нэг ажилтанд хоёр нээлттэй мөр
 * ! бий). Мөрийн талбар хоосон бол `current_*`-аас нөхнө. Хамарсан мөр байхгүй бол `current_*`.
 */
export const workSchedulePlacementOn = (
  date: string,
  resumes: readonly WorkScheduleResumeRow[],
  current: { departmentId: string | null; jobPositionId: string | null }
): WorkSchedulePlacement => {
  let best: WorkScheduleResumeRow | null = null

  for (const row of resumes) {
    if (!row.startDate || row.startDate > date) continue
    if (row.endDate && row.endDate < date) continue
    if (!best || compareIds(row.id, best.id) > 0) best = row
  }

  if (!best) return { ...current, from: 'current', resumeId: null }

  return {
    departmentId: best.departmentId ?? current.departmentId,
    jobPositionId: best.jobPositionId ?? current.jobPositionId,
    from: 'resume',
    resumeId: best.id
  }
}

/** BigInt мөрийг тоогоор харьцуулна (урт → их). */
const compareIds = (a: string, b: string): number => {
  if (a.length !== b.length) return a.length - b.length

  return a < b ? -1 : a > b ? 1 : 0
}

/* ─────────────────────────── Өдрийн хэв маяг ─────────────────────────── */

export type WorkScheduleDay = {
  weekday: WorkScheduleWeekday
  isWorkDay: boolean
  /** `HH:MM` — ажлын өдөр биш бол `null`. Олон мөртэй (эх системийн өглөө/үдээс хойш) өдөр: `min(hour_from)`. */
  start: string | null
  /** `HH:MM` — олон мөртэй өдөр: `max(hour_to)`. */
  end: string | null
  /** Завсарлага минутаар: тодорхой `break_from–break_to` + олон мөрийн хоорондох зай. */
  breakMinutes: number
  graceMinutes: number
  /** Энэ өдрийг тодорхойлсон хэв маягийн хувилбар (`valid_from`). Мөр огт байхгүй бол `null`. */
  versionFrom: string | null
}

/** Хуанлийн D өдөрт хүчинтэй хувилбар — `valid_from ≤ D` хамгийн сүүлийнх. Хувилбар бүр БҮТЭН 7 хоног. */
export const workScheduleVersionOn = (
  rows: readonly WorkSchedulePatternRow[],
  date: string
): string | null => {
  let version: string | null = null

  for (const row of rows) if (row.validFrom <= date && (version === null || row.validFrom > version)) version = row.validFrom

  return version
}

/**
 * D өдөрт хүчинтэй хувилбарын хоцролтын хөнгөлөлт — хувилбарын мөрөөс, байхгүй бол `fallback` (хуанлийн кэш).
 * ! Хөнгөлөлт ОГНООТОЙ (хэв маягтай хамт хувилбарлагдана) — PATCH-ээр хуанлийн баганыг солиод өнгөрсөн сарыг
 * ! чимээгүй дахин бодох зам байхгүй.
 */
export const workScheduleGraceOn = (
  rows: readonly WorkSchedulePatternRow[],
  date: string,
  fallback: number
): number => {
  const version = workScheduleVersionOn(rows, date)

  if (version === null) return fallback

  for (const row of rows) {
    if (row.validFrom === version && typeof row.graceMinutes === 'number') return row.graceMinutes
  }

  return fallback
}

/**
 * Хуанлийн D өдрийн хуваарь.
 *
 * ! ХУВИЛБАР НЬ БҮХ 7 ХОНОГИЙНХ: `valid_from ≤ D` хамгийн сүүлийн хувилбарын мөрүүдийг л авна (засварлагч 7 мөрийг НЭГ
 * ! хувилбараар бичдэг). Тэр хувилбарт тухайн гарагийн мөр байхгүй бол АМРАЛТЫН өдөр — хуучин хувилбарын мөр «цоолж» гарахгүй.
 * ! Олон мөртэй өдөр: `start = min`, `end = max` (эх системийн `find()` ORDER BY-гүй тул санамсаргүй мөр авдаг байсныг тодорхой
 * ! болгоно), мөр хоорондын зай нь завсарлага.
 */
export const workScheduleDayOf = (calendar: WorkScheduleCalendarInput, date: string): WorkScheduleDay => {
  const weekday = workScheduleWeekday(date)
  const version = workScheduleVersionOn(calendar.rows, date)
  const rest: WorkScheduleDay = {
    weekday,
    isWorkDay: false,
    start: null,
    end: null,
    breakMinutes: 0,
    graceMinutes: workScheduleGraceOn(calendar.rows, date, calendar.graceMinutes),
    versionFrom: version
  }

  if (version === null) return rest

  const spans = calendar.rows
    .filter(row => row.validFrom === version && row.weekday?.trim().toLowerCase() === weekday && row.isWorkDay)
    .map(row => ({
      from: normalizeWorkScheduleTime(row.hourFrom),
      to: normalizeWorkScheduleTime(row.hourTo),
      breakFrom: normalizeWorkScheduleTime(row.breakFrom ?? null),
      breakTo: normalizeWorkScheduleTime(row.breakTo ?? null)
    }))
    .filter(
      (span): span is { from: string; to: string; breakFrom: string | null; breakTo: string | null } =>
        span.from !== null && span.to !== null && workScheduleMinutes(span.to) > workScheduleMinutes(span.from)
    )
    .sort((a, b) => workScheduleMinutes(a.from) - workScheduleMinutes(b.from))

  if (spans.length === 0) return rest

  let breakMinutes = 0
  let reach = workScheduleMinutes(spans[0]!.to)

  for (const [index, span] of spans.entries()) {
    if (span.breakFrom && span.breakTo) {
      const gap = workScheduleMinutes(span.breakTo) - workScheduleMinutes(span.breakFrom)

      if (gap > 0) breakMinutes += gap
    }

    if (index > 0) {
      const from = workScheduleMinutes(span.from)

      if (from > reach) breakMinutes += from - reach

      reach = Math.max(reach, workScheduleMinutes(span.to))
    }
  }

  return {
    ...rest,
    isWorkDay: true,
    start: spans[0]!.from,
    end: spans.reduce((max, span) => (workScheduleMinutes(span.to) > workScheduleMinutes(max) ? span.to : max), spans[0]!.to),
    breakMinutes
  }
}

/** Засварлагч/жагсаалтад: D өдөрт хүчинтэй хувилбарын 7 өдөр, Даваагаас. */
export type WorkScheduleWeekDay = {
  weekday: WorkScheduleWeekday
  isWorkDay: boolean
  start: string | null
  end: string | null
  breakFrom: string | null
  breakTo: string | null
}

/**
 * D өдөрт хүчинтэй хувилбарын долоо хоног (засварлагчийн мөрүүд, JSON кэш). Олон мөртэй хуучин өдөр: min/max, завсарлага нь
 * мөр хоорондын ЭХНИЙ зай (засварлагчид ганц завсарлагын талбар бий).
 */
export const workScheduleWeekOn = (
  rows: readonly WorkSchedulePatternRow[],
  date: string
): { versionFrom: string | null; days: WorkScheduleWeekDay[] } => {
  const versionFrom = workScheduleVersionOn(rows, date)

  const days = WORK_SCHEDULE_WEEKDAYS.map(weekday => {
    const spans = rows
      .filter(row => row.validFrom === versionFrom && row.weekday?.trim().toLowerCase() === weekday && row.isWorkDay)
      .map(row => ({
        from: normalizeWorkScheduleTime(row.hourFrom),
        to: normalizeWorkScheduleTime(row.hourTo),
        breakFrom: normalizeWorkScheduleTime(row.breakFrom ?? null),
        breakTo: normalizeWorkScheduleTime(row.breakTo ?? null)
      }))
      .filter(
        (span): span is { from: string; to: string; breakFrom: string | null; breakTo: string | null } =>
          span.from !== null && span.to !== null
      )
      .sort((a, b) => workScheduleMinutes(a.from) - workScheduleMinutes(b.from))

    if (spans.length === 0) {
      return { weekday, isWorkDay: false, start: null, end: null, breakFrom: null, breakTo: null }
    }

    const first = spans[0]!
    const end = spans.reduce((max, span) => (workScheduleMinutes(span.to) > workScheduleMinutes(max) ? span.to : max), first.to)
    const gap = spans.length > 1 ? { breakFrom: first.to, breakTo: spans[1]!.from } : null

    return {
      weekday,
      isWorkDay: true,
      start: first.from,
      end,
      breakFrom: first.breakFrom ?? gap?.breakFrom ?? null,
      breakTo: first.breakTo ?? gap?.breakTo ?? null
    }
  })

  return { versionFrom, days }
}

/* ─────────────────────────── Резолвер ─────────────────────────── */

/** «Яагаад энэ хуваарь?» — шалгасан түвшин бүр, эрэмбээр. */
export type WorkScheduleChainStep = {
  source: WorkScheduleScope
  /** Шалгасан объект (ажилтан, тушаал, алба, байгууллага); `global` бол `null`. */
  targetId: string | null
  /** `department` түвшинд: өвлөсөн бол 1+ (эцэг = 1). Бусад түвшинд 0. */
  depth: number
  assignmentId: string | null
  calendarId: string | null
  /** `matched` — энэ алхам хуваарийг шийдсэн; `missing-calendar` — хуваарилалт бий ч хуанли нь алга/устсан (дараагийн түвшин рүү). */
  outcome: 'matched' | 'no-assignment' | 'missing-calendar' | 'no-target'
}

export type WorkScheduleResolveInput = {
  date: string
  employee: { id: string; organizationId: string | null }
  placement: { departmentId: string | null; jobPositionId: string | null }
  departments: readonly WorkScheduleDepartmentNode[]
  /**
   * `departments`-ийн `id → мөр` толь, УРЬДЧИЛАН бүтээсэн (заавал биш). Өгвөл дуудалт бүрд бүх албаар Map бүтээхгүй —
   * нэг ажилтны олон өдрийг бодоход (сарын тайлан, BI 13 сар) гол өртөг энэ байв.
   */
  departmentById?: ReadonlyMap<string, WorkScheduleDepartmentNode>
  /** Бүх хуваарилалт (устгагдаагүй) — огноогоор энд шүүнэ. */
  assignments: readonly WorkScheduleAssignmentInput[]
  calendars: ReadonlyMap<string, WorkScheduleCalendarInput>
}

export type WorkScheduleResolution = {
  date: string
  source: WorkScheduleSource
  calendarId: string | null
  calendarName: string | null
  assignmentId: string | null
  /** Албанаас өвлөсөн бол хуваарилалт хийгдсэн өвөг алба. */
  inheritedFromDepartmentId: string | null
  organizationId: string | null
  day: WorkScheduleDay | null
  chain: WorkScheduleChainStep[]
}

/** Хуваарилалт D өдөрт хүчинтэй эсэх: `valid_from ≤ D ≤ valid_to` (`valid_to` хоосон = нээлттэй). Хоёр хил ОРНО. */
export const workScheduleAssignmentCovers = (
  assignment: Pick<WorkScheduleAssignmentInput, 'validFrom' | 'validTo'>,
  date: string
): boolean => assignment.validFrom <= date && (assignment.validTo === null || date <= assignment.validTo)

/**
 * Нэг түвшний нэг объектод D өдөрт хүчинтэй хуваарилалт. Давхцал сервис дээр хоригдсон ч гажиг өгөгдөлд ТОДОРХОЙ байхын тулд:
 * хамгийн сүүлийн `valid_from`, тэнцвэл хамгийн том id.
 */
const assignmentFor = (
  assignments: readonly WorkScheduleAssignmentInput[],
  scope: WorkScheduleScope,
  targetId: string | null,
  date: string
): WorkScheduleAssignmentInput | null => {
  let best: WorkScheduleAssignmentInput | null = null

  for (const row of assignments) {
    if (row.scope !== scope) continue
    if (scope !== 'global' && (targetId === null || String(row.targetId) !== String(targetId))) continue
    if (!workScheduleAssignmentCovers(row, date)) continue
    if (
      !best ||
      row.validFrom > best.validFrom ||
      (row.validFrom === best.validFrom && compareIds(String(row.id), String(best.id)) > 0)
    ) {
      best = row
    }
  }

  return best
}

/**
 * Ажилтны D өдрийн ажлын хуваарь — давуу эрэмбээр (файлын толгойг үзнэ үү).
 *
 * ! `placement`-ийг дуудагч `workSchedulePlacementOn(D, resumes, current)`-ээр бодож өгнө.
 */
export const resolveWorkSchedule = (input: WorkScheduleResolveInput): WorkScheduleResolution => {
  const { date, calendars, assignments } = input
  const chain: WorkScheduleChainStep[] = []
  const departmentById = input.departmentById ?? new Map(input.departments.map(row => [String(row.id), row]))

  const organizationId =
    input.employee.organizationId ??
    (input.placement.departmentId ? effectiveDepartmentOrganization(departmentById, input.placement.departmentId) : null)

  const finish = (
    step: WorkScheduleChainStep,
    assignment: WorkScheduleAssignmentInput,
    calendar: WorkScheduleCalendarInput,
    inheritedFromDepartmentId: string | null
  ): WorkScheduleResolution => ({
    date,
    source: step.source,
    calendarId: calendar.id,
    calendarName: calendar.name,
    assignmentId: assignment.id,
    inheritedFromDepartmentId,
    organizationId,
    day: workScheduleDayOf(calendar, date),
    chain
  })

  /** Нэг алхам — олдвол үр дүн, үгүй бол `null` (дараагийн түвшин). */
  const tryStep = (
    source: WorkScheduleScope,
    targetId: string | null,
    depth: number,
    inheritedFromDepartmentId: string | null = null
  ): WorkScheduleResolution | null => {
    if (source !== 'global' && targetId === null) {
      chain.push({ source, targetId, depth, assignmentId: null, calendarId: null, outcome: 'no-target' })

      return null
    }

    const assignment = assignmentFor(assignments, source, targetId, date)

    if (!assignment) {
      chain.push({ source, targetId, depth, assignmentId: null, calendarId: null, outcome: 'no-assignment' })

      return null
    }

    const calendar = calendars.get(String(assignment.calendarId))

    if (!calendar) {
      chain.push({
        source,
        targetId,
        depth,
        assignmentId: assignment.id,
        calendarId: assignment.calendarId,
        outcome: 'missing-calendar'
      })

      return null
    }

    const step: WorkScheduleChainStep = {
      source,
      targetId,
      depth,
      assignmentId: assignment.id,
      calendarId: calendar.id,
      outcome: 'matched'
    }

    chain.push(step)

    return finish(step, assignment, calendar, inheritedFromDepartmentId)
  }

  const employee = tryStep('employee', input.employee.id, 0)

  if (employee) return employee

  const job = tryStep('job_position', input.placement.jobPositionId, 0)

  if (job) return job

  if (input.placement.departmentId === null) {
    chain.push({ source: 'department', targetId: null, depth: 0, assignmentId: null, calendarId: null, outcome: 'no-target' })
  } else {
    const seen = new Set<string>()
    let cursor: string | null = String(input.placement.departmentId)
    let depth = 0

    while (cursor !== null && !seen.has(cursor) && depth <= WORK_SCHEDULE_DEPARTMENT_MAX_DEPTH) {
      seen.add(cursor)

      const found = tryStep('department', cursor, depth, depth > 0 ? cursor : null)

      if (found) return found

      const parent: string | null | undefined = departmentById.get(cursor)?.parentId

      cursor = parent === null || parent === undefined ? null : String(parent)
      depth += 1
    }
  }

  const organization = tryStep('organization', organizationId, 0)

  if (organization) return organization

  const global = tryStep('global', null, 0)

  if (global) return global

  return {
    date,
    source: 'none',
    calendarId: null,
    calendarName: null,
    assignmentId: null,
    inheritedFromDepartmentId: null,
    organizationId,
    day: null,
    chain
  }
}

/* ─────────────────────────── API ↔ админы хэлбэр ─────────────────────────── */

/** `GET /hr/work-schedules` мөр. */
export type WorkScheduleListRow = {
  id: string
  name: string
  code: string | null
  description: string | null
  graceMinutes: number
  isActive: boolean
  /** Өнөөдөр (УБ) хүчинтэй хувилбарын 7 өдөр, Даваагаас. */
  versionFrom: string | null
  days: WorkScheduleWeekDay[]
  /** Өнөөдөр хүчинтэй + ирээдүйн хуваарилалтын тоо — түвшнээр. */
  usage: Record<WorkScheduleScope, number>
  /**
   * Устгагдаагүй БҮХ хуваарилалт (дууссаныг оруулаад). > 0 бол хуваарийг устгахгүй — өнгөрсөн өдрүүд энэ хуваарийг
   * заасаар байх ёстой (дахин бодоход түүх өөрчлөгдөхгүй).
   */
  assignmentsTotal: number
  /** Энэ хуваарь глобал өгөгдмөл эсэх (өнөөдөр). */
  isGlobalDefault: boolean
}

/** `GET /hr/work-schedules/:id` — одоогийн хэв маяг + хувилбарын түүх. */
export type WorkScheduleDetail = WorkScheduleListRow & {
  versions: { validFrom: string; graceMinutes: number; days: WorkScheduleWeekDay[] }[]
}

/** `PUT /hr/work-schedules/:id/week` ба `POST`-ын `days[7]`. */
export type WorkScheduleWeekDayInput = {
  weekday: WorkScheduleWeekday
  isWorkDay: boolean
  start?: string | null
  end?: string | null
  breakFrom?: string | null
  breakTo?: string | null
}

/** `PUT /hr/work-schedules/:id/week` бие (`POST`-ын `graceMinutes` нь эхний хувилбарынх). */
export type WorkScheduleWeekInput = {
  validFrom: string
  days: WorkScheduleWeekDayInput[]
  /** Энэ хувилбарын хөнгөлөлт. Орхивол `validFrom` өдөр хүчинтэй байсан хувилбарынхыг авна. */
  graceMinutes?: number
  confirmBackdate?: boolean
}

/** `GET /hr/work-schedule-assignments` мөр. */
export type WorkScheduleAssignmentRow = {
  id: string
  scope: WorkScheduleScope
  targetId: string | null
  targetName: string | null
  calendar: { id: string; name: string }
  validFrom: string
  validTo: string | null
  note: string | null
  createdAt: string
  createdBy: { id: string; name: string | null } | null
  /** Өнөөдөр (УБ) хүчинтэй эсэх / ирээдүйд эхлэх эсэх. */
  status: 'active' | 'future' | 'past'
}

/** `PATCH /hr/work-schedule-assignments/:id` бие — хуваарилалтыг огнооноос ДУУСГАХ (`validTo`), `null` = нээх. */
export type WorkScheduleAssignmentEndInput = {
  validTo: string | null
  confirmBackdate?: boolean
}

/** `POST` / `PATCH /hr/work-schedule-assignments` хариу. */
export type WorkScheduleAssignmentWriteResult = {
  assignment: WorkScheduleAssignmentRow
  /** Хаагдсан өмнөх нээлттэй мөр (`valid_to = valid_from − 1`). */
  closedAssignmentId: string | null
  /** Бэкдейт үед `valid_from`-оос өнөөдөр хүртэлх, тухайн хүрээний ажилтнуудын өдрийн тайлангийн тоо (автоматаар ДАХИН БОДОХГҮЙ). */
  affectedReports: number
  backdated: boolean
}

/** `GET /hr/work-schedules/resolve` хариу. */
export type WorkScheduleResolveResponse = WorkScheduleResolution & {
  employeeId: string
  placement: WorkSchedulePlacement
  /** `chain`-ийн объектуудын нэр (админы «Албан тушаалаас: ХН менежер 09:00» тайлбарт). */
  names: {
    departments: Record<string, string>
    jobPositions: Record<string, string>
    organizations: Record<string, string>
    calendars: Record<string, string>
  }
}

/** `GET /hr/options/work-schedules` мөр. */
export type WorkScheduleOption = {
  id: string
  name: string
  code: string | null
  /** Товч тайлбар: «Да–Ба 08:00–17:00». */
  summary: string
}

/** Товч тайлбар — «Да–Ба 08:00–17:00», өөр цагтай өдрүүд таслалаар. Админы жагсаалт ба сонголтод НЭГ дүрэм. */
export const WORK_SCHEDULE_WEEKDAY_SHORT_MN: Record<WorkScheduleWeekday, string> = {
  monday: 'Да',
  tuesday: 'Мя',
  wednesday: 'Лх',
  thursday: 'Пү',
  friday: 'Ба',
  saturday: 'Бя',
  sunday: 'Ня'
}

export const summarizeWorkScheduleWeek = (days: readonly WorkScheduleWeekDay[]): string => {
  const groups: { from: WorkScheduleWeekday; to: WorkScheduleWeekday; hours: string }[] = []
  let previousIndex = -2

  for (const day of days) {
    if (!day.isWorkDay || !day.start || !day.end) {
      previousIndex = -2
      continue
    }

    const index = WORK_SCHEDULE_WEEKDAYS.indexOf(day.weekday)
    const hours = `${day.start}–${day.end}`
    const last = groups[groups.length - 1]

    if (last && last.hours === hours && index === previousIndex + 1) last.to = day.weekday
    else groups.push({ from: day.weekday, to: day.weekday, hours })

    previousIndex = index
  }

  if (groups.length === 0) return '—'

  return groups
    .map(group =>
      group.from === group.to
        ? `${WORK_SCHEDULE_WEEKDAY_SHORT_MN[group.from]} ${group.hours}`
        : `${WORK_SCHEDULE_WEEKDAY_SHORT_MN[group.from]}–${WORK_SCHEDULE_WEEKDAY_SHORT_MN[group.to]} ${group.hours}`
    )
    .join(', ')
}
